/**
 * Tab 6 — "Amort / Transaction Calendar" (Task 8, plan §5.6, Phase 10B).
 *
 * Read-only, single endpoint:
 *   GET .../calendar/<YYYY-MM-DD>/?include_no_schedule=<bool>
 *       CalendarDayView:2174 ->
 *       {date, include_no_schedule, entries: [...], no_schedule_loans: [...]}
 *
 * The shadcn `Calendar` (react-day-picker) defaults to TODAY selected, and the
 * `include_no_schedule` checkbox defaults to UNCHECKED per the plan's explicit
 * decision (also the backend's default — views_staff_ops.py:2216). Changing
 * either re-keys the query, so react-query refetches automatically; no manual
 * effect wiring needed.
 *
 * The selected `Date` is formatted with `toDateInputValue()` (local calendar
 * fields, NOT `toISOString()`) so a date picked by a user east of Greenwich
 * isn't shifted a day backwards by the UTC conversion.
 *
 * Loan IDs use the same <LoanIdLink> cross-tab jump as Tabs 4 and 5. Unlike
 * Tasks 6/7 this endpoint intentionally INCLUDES SIM_ loans (plan §5.6 Task 8
 * describes it as global across all loans), which is surfaced in the caption.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, RefreshCcw, Info } from 'lucide-react';

import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import lmsStaffService, {
    CalendarEntry,
    CalendarEntryStatus,
    getLmsApiErrorMessage,
} from './lmsStaffApi';
import { formatAmount, formatDate, toDateInputValue } from './formatters';
import { LoanIdLink } from './lmsStaffNav';

export interface CalendarOverviewTabProps {
    /**
     * can_edit for `calendar`, passed down by LmsStaffOpsHub. Accepted for
     * prop-contract consistency; Task 8 is read-only so it is unused.
     */
    canEdit?: boolean;
}

/**
 * The five statuses from views_staff_ops.py's CAL_STATUS_* constants, each with
 * a distinct colour: PAID=green, PARTIALLY_PAID=blue, OVERDUE=red, DUE=yellow,
 * NO_BILL_RAISED=gray.
 */
const STATUS_CLASSES: Record<CalendarEntryStatus, string> = {
    PAID: 'border-green-200 bg-green-50 text-green-700',
    PARTIALLY_PAID: 'border-blue-200 bg-blue-50 text-blue-700',
    OVERDUE: 'border-red-200 bg-red-50 text-red-700',
    DUE: 'border-amber-200 bg-amber-50 text-amber-700',
    NO_BILL_RAISED: 'border-gray-200 bg-gray-50 text-gray-600',
};

const CalendarStatusBadge: React.FC<{ status: CalendarEntryStatus }> = ({ status }) => (
    <Badge
        variant="outline"
        className={`text-xs ${
            STATUS_CLASSES[status] || 'border-gray-200 bg-gray-50 text-gray-600'
        }`}
    >
        {status}
    </Badge>
);

const CalendarOverviewTab: React.FC<CalendarOverviewTabProps> = () => {
    // Default: today's date selected (plan §8 "calendar widget ... defaulting to today").
    const [selectedDate, setSelectedDate] = React.useState<Date>(() => new Date());
    // Default: UNCHECKED, per the plan's explicit decision.
    const [includeNoSchedule, setIncludeNoSchedule] = React.useState(false);

    const dateStr = toDateInputValue(selectedDate);

    const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
        queryKey: ['lms-staff-calendar-day', dateStr, includeNoSchedule],
        queryFn: async () => {
            const response = await lmsStaffService.getCalendarDay(
                dateStr,
                includeNoSchedule
            );
            return response.data;
        },
    });

    const entries: CalendarEntry[] = data?.entries ?? [];
    const noScheduleLoans = data?.no_schedule_loans ?? [];

    return (
        <div className="w-full space-y-4">
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                <div>
                    <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900">
                        <CalendarDays className="h-5 w-5 text-intellend-green" />
                        Amortisation / Transaction Calendar
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Everything scheduled or settled on one date, across all loans
                        (simulator loans included).
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => refetch()}
                >
                    <RefreshCcw className="h-4 w-4" /> Refresh
                </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[auto,1fr]">
                {/* ── Date picker + toggle ──────────────────────────────── */}
                <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <Calendar
                        mode="single"
                        selected={selectedDate}
                        // react-day-picker passes `undefined` when the selected day
                        // is clicked again (deselect) — keep the current date rather
                        // than leaving the tab with no date to query.
                        onSelect={(day) => setSelectedDate(day ?? selectedDate)}
                        className="rounded-md border-0 p-0"
                    />
                    <div className="border-t pt-3">
                        <div className="flex items-start gap-2">
                            <Checkbox
                                id="include-no-schedule"
                                checked={includeNoSchedule}
                                onCheckedChange={(checked) =>
                                    setIncludeNoSchedule(checked === true)
                                }
                                className="mt-0.5"
                            />
                            <Label
                                htmlFor="include-no-schedule"
                                className="cursor-pointer text-sm font-normal"
                            >
                                Include loans with no scheduled payment on this date
                            </Label>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500">
                        Showing {formatDate(dateStr)}
                    </p>
                </div>

                {/* ── Entries ───────────────────────────────────────────── */}
                <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-gray-800">
                            Scheduled Payments{' '}
                            <span className="font-normal text-gray-500">
                                ({entries.length})
                            </span>
                        </h3>
                        {isFetching && !isLoading && (
                            <span className="text-xs text-gray-400">Refreshing...</span>
                        )}
                    </div>

                    {isError ? (
                        <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-600">
                            {getLmsApiErrorMessage(
                                error,
                                'Unable to load the calendar for this date.'
                            )}
                        </div>
                    ) : isLoading ? (
                        <Skeleton className="h-40 w-full" />
                    ) : entries.length === 0 ? (
                        <div className="py-12 text-center text-sm text-gray-500">
                            No amortisation rows fall on {formatDate(dateStr)}.
                        </div>
                    ) : (
                        <div className="max-h-[32rem] overflow-auto">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                                    <tr>
                                        <th className="px-3 py-2">Loan ID</th>
                                        <th className="px-3 py-2">Series</th>
                                        <th className="px-3 py-2">Payment Date</th>
                                        <th className="px-3 py-2">Amount</th>
                                        <th className="px-3 py-2">Principal</th>
                                        <th className="px-3 py-2">Interest</th>
                                        <th className="px-3 py-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((entry, index) => (
                                        <tr
                                            key={`${entry.intellend_loan_id}-${
                                                entry.loan_series ?? index
                                            }`}
                                            className="border-t border-gray-100"
                                        >
                                            <td className="px-3 py-2">
                                                <LoanIdLink
                                                    loanId={entry.intellend_loan_id}
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-gray-700">
                                                {entry.loan_series ?? '-'}
                                            </td>
                                            <td className="px-3 py-2 text-gray-700">
                                                {formatDate(entry.payment_date)}
                                            </td>
                                            <td className="px-3 py-2 text-gray-700">
                                                {formatAmount(entry.payment_amount)}
                                            </td>
                                            <td className="px-3 py-2 text-gray-700">
                                                {formatAmount(entry.payment_amount_p)}
                                            </td>
                                            <td className="px-3 py-2 text-gray-700">
                                                {formatAmount(entry.payment_amount_i)}
                                            </td>
                                            <td className="px-3 py-2">
                                                <CalendarStatusBadge
                                                    status={entry.status}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Informational: only requested/returned when the box is ticked */}
                    {includeNoSchedule && noScheduleLoans.length > 0 && (
                        <div className="space-y-2 border-t pt-3">
                            <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                <Info className="h-3.5 w-3.5" /> No payment scheduled today
                                ({noScheduleLoans.length})
                            </h4>
                            <ul className="divide-y divide-gray-100 text-sm">
                                {noScheduleLoans.map((loan) => (
                                    <li
                                        key={loan.intellend_loan_id}
                                        className="flex flex-wrap items-center gap-x-6 gap-y-1 py-2"
                                    >
                                        <LoanIdLink loanId={loan.intellend_loan_id} />
                                        <span className="text-xs text-gray-500">
                                            customer: {loan.lender_customer_id || '-'}
                                        </span>
                                        <Badge variant="outline" className="text-xs">
                                            {loan.current_status || 'UNKNOWN'}
                                        </Badge>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CalendarOverviewTab;
